import Breadcrumbs from 'components/breadcrumbs';
import Headline from 'components/headline';
import { config } from 'config';
import { PRODUCT_FEED_HANDLES } from 'config/collections/feed-handles';
import { getHandle } from 'config/collections/utils/get-handle';
import { useConfig } from 'config/use-config';
import { PdEnv } from 'constants/pd-env';
import { useAddItemsByUrl } from 'hooks/cart/use-add-items-by-url';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import useMobile from 'hooks/common/use-mobile';
import { usePreserveScroll } from 'hooks/common/use-preserve-scroll';
import useCollectionReady from 'hooks/gtm/use-collection-ready';
import { useBreadcrumbs } from 'hooks/product/use-breadcrumbs';
import { showPetProfileSelector } from 'modules/collection/utils';
import { useContentful } from 'modules/content-cms/components/context';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { isEmptyProfile } from 'stores/pet-profile-new/utils/is-empty-profile';
import { useRootStore } from 'stores/use-root-store';
import { isGenericCdpPage } from 'utils/api/smartCollection';
import { showSuccessToast } from 'utils/toasts';
import { getSegmentationFromUrl } from 'utils/url/get-segmentation-from-url';
import ContentCmsContentStatic from '../content-cms/components/content';
import ContentCmsHeaderContentStatic from '../content-cms/components/header-content';
import ProductsGrid from './components/products-grid';
import ResultCounter from './components/result-counter';
import { useCollectionState } from './state';

const TinyCart = dynamic(() => import('modules/cart/components/tiny-cart'), {
  ssr: false,
});

const VARIANT_SELECTOR_CDP =
  config.pdEnv === PdEnv.PRODUCTION
    ? 'saucen-menu-fur-katzen-bundle-33-bundle-sizes'
    : getHandle({ key: 'catsWetFood' });

const VariantSelectorAsync = dynamic(() => import('./components/variant-selector-wrapper'));

const PetProfileCollectionSelector = dynamic(
  () => import('components/pet-profile/collection-selector')
);

interface Input {
  hasH1title: boolean;
  setApplyPetProfile: (value: boolean) => void;
  handle: string;
  /** Url query to be added to product cart */
  additionalQuery?: Record<string, string>;
}

const HAS_ALLERGY_FILTERS_HANDLE = [
  getHandle({ key: 'dogsIntolerances' }),
  getHandle({ key: 'dogsIntolerancesLp' }),
  getHandle({ key: 'dogsIntolerancesLp1' }),
  getHandle({ key: 'dogsIntolerancesLp2' }),
];
const BLACK_DEALS_HANDLE = [
  getHandle({ key: 'dogsBlackWeekend', handles: PRODUCT_FEED_HANDLES }),
  getHandle({ key: 'catsBlackWeekend', handles: PRODUCT_FEED_HANDLES }),
];

const Collection: FC<Input> = ({ hasH1title, setApplyPetProfile, handle, additionalQuery }) => {
  const { featureFlags } = useConfig();
  const { query, isReady } = useRouter();
  const petType = useRootStore((store) => store.petType);
  const { collection, visibleProducts } = useCollectionState();
  const { breadcrumbList, isLoading } = useBreadcrumbs({ product: undefined });
  const showDynamicUx = featureFlags.dynamicUxTest;
  const featureFlag = featureFlags.petProfile;
  const petProfile = useRootStore((store) => store.currentPetProfile);

  const showPetSelector = useMemo(() => {
    if (!showDynamicUx) return false;
    return (
      isGenericCdpPage({ url: `/collections/${handle}` }) ||
      getSegmentationFromUrl({ path: `/collections/${handle}` })
    );
  }, [handle, showDynamicUx]);

  const { isEc } = useIsExistingCustomer();
  const { data: contentful } = useContentful();

  // Use the `handle` prop (stable across server/client) instead of router.query,
  // which is empty on the server for static routes and caused a hydration mismatch.
  const shouldShowAllergyFilter = HAS_ALLERGY_FILTERS_HANDLE.some(
    (handleName) => handleName === handle
  );

  // Check if the collection is a black deals collection. Should be removed after the black deals campaign
  const shouldShowBlackRibbon = Boolean(
    isEc &&
    isReady &&
    query.all?.[0] &&
    BLACK_DEALS_HANDLE.some((handleName) => handleName === query.all?.[0])
  );

  const isMobile = useMobile();

  const shouldInjectVariantSelector = useMemo(
    () => Boolean(isReady && query.all?.[0] === VARIANT_SELECTOR_CDP),
    [query, isReady]
  );

  const location = query.all?.[0] || '';
  const [showTinyCart, setShowTinyCart] = useState(false);
  const { addedItems } = useAddItemsByUrl();

  useEffect(() => {
    if (addedItems.length > 0) {
      showSuccessToast({
        message: 'cdp:toast:add-single-item',
      });

      setShowTinyCart(true);
    }
  }, [addedItems.length, setShowTinyCart]);

  /** Send ready event only once per life cycle, excluding filter updates or SWR update */
  const [shouldTrigger, setShouldTrigger] = useState<boolean>(true);
  const handleRef = useRef(false);
  useEffect(() => {
    if (handleRef.current) {
      return;
    }
    handleRef.current = true;
    setShouldTrigger(false);
  }, []);

  useCollectionReady({
    shouldTrigger,
    products: collection.products,
    collectionTitle: collection.title,
  });

  usePreserveScroll(!!collection.title);

  return (
    <>
      {showTinyCart && (
        <TinyCart
          onCloseCallback={() => {
            setShowTinyCart(false);
          }}
        />
      )}
      {shouldInjectVariantSelector && isMobile && <VariantSelectorAsync />}

      <ContentCmsHeaderContentStatic priority />

      <div className="bg-other-white px-4 md:px-8">
        <div className="container px-0!" id="product-listing-section">
          <Breadcrumbs
            breadcrumbs={breadcrumbList}
            isLoading={isLoading}
            className="mb-3 lg:mt-3"
            dataTest="breadcrumbs-cdp"
          />
          <Headline
            title={(contentful && contentful.fields.headline) || collection.title}
            shouldBeH1={!hasH1title}
            classNames="lg:mb-5 mb-1.5"
            extraElement={
              <span className="hidden lg:block">
                <ResultCounter productCount={visibleProducts.length} />
              </span>
            }
            smallerFont
          />
          {showPetProfileSelector({
            showPetSelector,
            featureFlag,
            petType,
            hasPetProfile: !isEmptyProfile(petProfile),
          }) && (
            <PetProfileCollectionSelector
              location={location}
              setApplyPetProfile={setApplyPetProfile}
              collectionHandle={handle}
            />
          )}
          <ProductsGrid
            showFilter={true}
            contentfulData={contentful}
            shouldShowAllergyFilter={shouldShowAllergyFilter}
            additionalQuery={additionalQuery}
            shouldShowBlackRibbon={shouldShowBlackRibbon}
          />
        </div>
      </div>

      <div className="container mt-3 grid grid-cols-1 gap-5 lg:mt-8 lg:grid-cols-4">
        <div className="col-span-1" />
        <div className="col-span-1 lg:col-span-3">
          <ContentCmsContentStatic />
        </div>
      </div>
    </>
  );
};

export default Collection;
